/**
 * Confidence Scoring Service
 * Calculates fitness scores for response units based on context matching
 * Enables intelligent retrieval with AI fallback when confidence is low
 */

/**
 * Calculate confidence score for a response unit given request context
 * @param {Object} responseUnit - The response unit to score
 * @param {Object} context - Request context (persona, stage, industry, objectionType)
 * @returns {Object} - { score: 0-100, breakdown: {persona, stage, industry, objection} }
 */
export const calculateConfidence = (responseUnit, context) => {
  const breakdown = {
    persona: 0,
    stage: 0,
    industry: 0,
    objection: 0
  };

  const coverageMap = responseUnit.coverage_map || {};
  
  // Persona Match (25 points)
  if (context.persona && coverageMap.personas?.length > 0) {
    if (coverageMap.personas.includes(context.persona)) {
      breakdown.persona = 25;
    } else if (coverageMap.personas.includes('universal')) {
      breakdown.persona = 20; // Universal persona gets high but not perfect score
    }
  } else if (!context.persona || !coverageMap.personas) {
    // No persona requirement or no coverage data = neutral (12.5 points)
    breakdown.persona = 12.5;
  }

  // Sales Stage Match (25 points)
  if (context.stage && coverageMap.stages?.length > 0) {
    if (coverageMap.stages.includes(context.stage)) {
      breakdown.stage = 25;
    } else if (coverageMap.stages.includes('all_stages')) {
      breakdown.stage = 20;
    }
  } else if (!context.stage || !coverageMap.stages) {
    breakdown.stage = 12.5;
  }

  // Industry Match (25 points)
  if (context.industry && coverageMap.industries?.length > 0) {
    if (coverageMap.industries.includes(context.industry)) {
      breakdown.industry = 25;
    } else if (coverageMap.industries.includes('cross_industry')) {
      breakdown.industry = 22; // Cross-industry applicable gets high score
    }
  } else if (!context.industry || !coverageMap.industries) {
    breakdown.industry = 12.5;
  }

  // Objection Type Match (25 points) - Only relevant for objection responses
  if (responseUnit.type === 'objection' && context.objectionType) {
    if (coverageMap.objection_types?.includes(context.objectionType)) {
      breakdown.objection = 25;
    } else if (coverageMap.objection_types?.includes('general')) {
      breakdown.objection = 15;
    }
  } else if (responseUnit.type !== 'objection') {
    // Not an objection response, so objection match is N/A - give neutral score
    breakdown.objection = 12.5;
  } else if (!context.objectionType) {
    breakdown.objection = 12.5;
  }

  const totalScore = Math.round(
    breakdown.persona + 
    breakdown.stage + 
    breakdown.industry + 
    breakdown.objection
  );

  return {
    score: Math.min(100, totalScore),
    breakdown,
    matchQuality: getMatchQuality(totalScore)
  };
};

/**
 * Get qualitative match quality label
 * @param {number} score - Confidence score 0-100
 * @returns {string} - Quality label
 */
const getMatchQuality = (score) => {
  if (score >= 90) return 'excellent';
  if (score >= 75) return 'good';
  if (score >= 60) return 'fair';
  if (score >= 40) return 'poor';
  return 'very_poor';
};

/**
 * Find best matching response units for given context
 * @param {Array} responseUnits - Array of response units to score
 * @param {Object} context - Request context
 * @param {number} minConfidence - Minimum confidence threshold (default 75)
 * @returns {Array} - Sorted array with confidence scores
 */
export const rankResponsesByConfidence = (responseUnits, context, minConfidence = 75) => {
  const scored = responseUnits.map(unit => {
    const confidence = calculateConfidence(unit, context);
    return {
      ...unit,
      confidence
    };
  });

  // Sort by confidence score descending
  scored.sort((a, b) => b.confidence.score - a.confidence.score);

  // Filter by minimum confidence if specified
  return scored.filter(item => item.confidence.score >= minConfidence);
};

/**
 * Check if response unit has adequate coverage for context
 * @param {Object} responseUnit - Response unit to check
 * @param {Object} context - Request context
 * @param {number} threshold - Minimum confidence threshold (default 75)
 * @returns {boolean} - True if confidence meets threshold
 */
export const hasAdequateCoverage = (responseUnit, context, threshold = 75) => {
  const { score } = calculateConfidence(responseUnit, context);
  return score >= threshold;
};

/**
 * Generate coverage map from response unit content and metadata
 * Used when normalizing AI-generated responses
 * @param {Object} responseUnit - Response unit with content and metadata
 * @returns {Object} - Coverage map { personas, stages, industries, objection_types }
 */
export const generateCoverageMap = (responseUnit) => {
  const coverageMap = {
    personas: [],
    stages: [],
    industries: [],
    objection_types: []
  };

  // Extract from metadata if present
  if (responseUnit.metadata?.persona) {
    coverageMap.personas.push(responseUnit.metadata.persona);
  }
  
  if (responseUnit.metadata?.sales_stage) {
    coverageMap.stages.push(responseUnit.metadata.sales_stage);
  }
  
  if (responseUnit.metadata?.industry) {
    coverageMap.industries.push(responseUnit.metadata.industry);
  }

  // For objection responses, extract objection type
  if (responseUnit.type === 'objection' && responseUnit.metadata?.objection_type) {
    coverageMap.objection_types.push(responseUnit.metadata.objection_type);
  }

  // Detect universal/cross-industry applicability from tags or content
  const tags = responseUnit.tags || [];
  if (tags.includes('universal') || tags.includes('cross-industry')) {
    if (!coverageMap.personas.includes('universal')) {
      coverageMap.personas.push('universal');
    }
    if (!coverageMap.industries.includes('cross_industry')) {
      coverageMap.industries.push('cross_industry');
    }
  }

  // If no specific stage, assume all stages
  if (coverageMap.stages.length === 0) {
    coverageMap.stages.push('all_stages');
  }

  return coverageMap;
};

export default {
  calculateConfidence,
  rankResponsesByConfidence,
  hasAdequateCoverage,
  generateCoverageMap
};
